import type { ApiEnvelope, TenantContext, TenantMembership } from '~/types/types'

interface TenantResolveOptions {
  slug?: string | null
  host?: string
}

export interface PublicTenantCard {
  slug: string
  name: string
  description: string | null
  logo_url: string | null
  cover_url: string | null
  domain: string
  is_custom_domain: boolean
  primary_color: string | null
  category: string | null
}

/**
 * Tenant-context API client. agency-saas is admin-led + subdomain-only:
 *   - Tenants are created via /api/admin/tenants (platform admin only)
 *   - Members are added via the admin user/tenant UIs
 *   - Public resolution + own-membership lookup are the only read paths
 *     the frontend needs here
 *
 * The zth-era community endpoints (/api/community/join, /api/community,
 * /api/community/me) are intentionally not wired — see the stubs below.
 */
export const useTenantRepository = () => {
  const api = useApi()

  const resolve = async (resolveOptions: TenantResolveOptions = {}): Promise<TenantContext | null> => {
    const query: Record<string, string> = {}
    if (resolveOptions.slug) {
      query.slug = resolveOptions.slug
    } else if (resolveOptions.host) {
      query.host = resolveOptions.host
    }

    const response = await api.get<ApiEnvelope<TenantContext | null>>('/api/public/tenant/resolve', {
      query: Object.keys(query).length ? query : undefined
    })

    return response.data
  }

  /**
   * "Where do I stand on this tenant?" — returns the authenticated user's
   * membership for the current tenant (resolved server-side via the
   * X-Tenant-Slug header injected by plugins/api.ts), or null when the
   * user has no row in tenant_users for this tenant.
   *
   * Backed by GET /api/membership/me. Replaces the zth-era /api/community/me.
   */
  const me = async (): Promise<TenantMembership | null> => {
    const response = await api.get<ApiEnvelope<TenantMembership | null>>('/api/membership/me')
    return response.data
  }

  /**
   * Public list of published tenant agencies for the platform directory
   * (powers the /demos page). Backed by GET /api/public/tenants; on any
   * error it returns an empty list so callers render an empty-state instead
   * of crashing.
   */
  const listPublic = async (): Promise<PublicTenantCard[]> => {
    try {
      const response = await api.get<ApiEnvelope<PublicTenantCard[]>>('/api/public/tenants')
      return response.data ?? []
    } catch {
      return []
    }
  }

  // ── No-op stubs for zth-era flows that don't exist in agency-saas ────
  // Self-serve join and self-serve tenant creation were removed when the
  // admin-led model was adopted (see useCommunityLinks.ts for rationale).
  // Methods kept on the surface so any straggling call site fails loud in
  // dev rather than silently breaking the build.

  const join = async (_payload?: unknown) => {
    if (import.meta.dev) {
      console.warn('[tenantRepository] join() not implemented — agency-saas has no self-serve join flow.')
    }
    return null
  }

  const createCommunity = async (_payload: unknown) => {
    if (import.meta.dev) {
      console.warn('[tenantRepository] createCommunity() not implemented — use /api/admin/tenants instead.')
    }
    return null
  }

  return {
    resolve,
    me,
    listPublic,
    join,
    createCommunity
  }
}
